<template>
  <Teleport to="body">
    <Transition name="lightbox">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col"
        @click="handleBackdropClick"
      >
        <!-- Header -->
        <div class="shrink-0 p-4 flex items-center justify-between">
          <!-- Image counter -->
          <div class="text-white text-sm font-medium">
            {{ currentIndex + 1 }} / {{ images.length }}
          </div>

          <!-- Close button -->
          <button
            @click="close"
            class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer"
            :aria-label="t('lightbox.close')"
          >
            <svg class="w-6 h-6 text-theme-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Main content -->
        <div class="flex-1 relative overflow-hidden flex items-center justify-center p-4">
          <!-- Previous button -->
          <button
            v-if="images.length > 1"
            @click.stop="previous"
            class="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            :aria-label="t('lightbox.previous')"
            :disabled="currentIndex === 0"
            :class="{ 'opacity-50 cursor-not-allowed': currentIndex === 0 }"
          >
            <svg class="w-6 h-6 text-theme-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>

          <!-- Image container with swipe support -->
          <div
            ref="imageContainer"
            class="relative w-full h-full flex items-center justify-center overflow-hidden select-none touch-pan-y"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
            @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseEnd"
            @mouseleave="handleMouseEnd"
          >
            <!-- Image/Video wrapper with fixed dimensions -->
            <div class="relative w-full h-full flex items-center justify-center">
              <Transition :name="transitionName" mode="out-in">
                <!-- Use CloudinaryMedia for videos to get proper video player -->
                <div
                  v-if="isCurrentVideo"
                  :key="currentIndex"
                  class="w-full max-w-4xl rounded-lg overflow-hidden"
                  :style="{ transform: isDragging ? `translateX(${swipeOffset}px)` : 'translateX(0)' }"
                >
                  <CloudinaryMedia
                    :public-id="currentImage.publicId"
                    :resource-type="'video'"
                    :alt="currentImage.filename || 'Video'"
                  />
                </div>
                <!-- Use img tag for images -->
                <img
                  v-else
                  :key="currentIndex"
                  :src="currentImageUrl"
                  :alt="currentImage.filename || 'Image'"
                  class="max-w-full max-h-full w-auto h-auto object-contain rounded-lg"
                  :style="{ transform: isDragging ? `translateX(${swipeOffset}px)` : 'translateX(0)' }"
                />
              </Transition>
            </div>
          </div>

          <!-- Next button -->
          <button
            v-if="images.length > 1"
            @click.stop="next"
            class="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            :aria-label="t('lightbox.next')"
            :disabled="currentIndex === images.length - 1"
            :class="{ 'opacity-50 cursor-not-allowed': currentIndex === images.length - 1 }"
          >
            <svg class="w-6 h-6 text-theme-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>

        <!-- Footer actions -->
        <div class="shrink-0 p-4 flex flex-col items-center gap-4 max-w-2xl mx-auto w-full">
          <!-- Privacy notice -->
          <div class="w-full bg-white/10 backdrop-blur-md rounded-lg p-4">
            <p class="text-theme-text text-sm leading-relaxed text-center">
              {{ t('privacy.notice') }}
            </p>
          </div>

          <!-- Download/Share button -->
          <button
            @click.stop="handleDownload"
            :disabled="downloading"
            class="w-full max-w-md flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-base font-semibold transition-all duration-200 disabled:opacity-50 button-primary disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
          >
            <svg v-if="!downloading" class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path v-if="canShare" d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4 4-4-4M12 2v10" stroke-linecap="round" stroke-linejoin="round"/>
              <path v-else d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <div v-else class="w-6 h-6 border-2 loading-spinner rounded-full animate-spin"></div>
            <span>{{ downloadButtonText }}</span>
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useLanguage } from '@composables/useLanguage'
import CloudinaryMedia from '@components/CloudinaryMedia.vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  images: {
    type: Array,
    required: true,
    default: () => []
  },
  initialIndex: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['close', 'indexChange'])

const { t } = useLanguage()

// State
const currentIndex = ref(props.initialIndex)
const downloading = ref(false)
const canShare = ref(false)
const imageContainer = ref(null)
const transitionName = ref('slide-left')

// Swipe/drag state
const swipeOffset = ref(0)
const startX = ref(0)
const isDragging = ref(false)

// Computed
const currentImage = computed(() => props.images[currentIndex.value] || {})
const currentImageUrl = computed(() => {
  const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME
  const publicId = currentImage.value.publicId
  const resourceType = currentImage.value.resourceType || 'image'
  if (!publicId) return ''
  return `https://res.cloudinary.com/${cloudName}/${resourceType}/upload/q_auto,f_auto/${publicId}`
})

const isCurrentVideo = computed(() => {
  return currentImage.value.resourceType === 'video'
})

const downloadButtonText = computed(() => {
  if (downloading.value) return t('download.loading')
  return canShare.value ? t('download.buttonShare') : t('download.buttonDownload')
})

// Methods
const close = () => {
  emit('close')
}

const previous = () => {
  if (currentIndex.value > 0) {
    transitionName.value = 'slide-right'
    currentIndex.value--
    emit('indexChange', currentIndex.value)
  }
}

const next = () => {
  if (currentIndex.value < props.images.length - 1) {
    transitionName.value = 'slide-left'
    currentIndex.value++
    emit('indexChange', currentIndex.value)
  }
}

const handleBackdropClick = (e) => {
  // Only close if clicking the backdrop, not the image
  if (e.target === e.currentTarget) {
    close()
  }
}

// Download/Share functionality
const handleDownload = async () => {
  if (!currentImageUrl.value || downloading.value) return

  downloading.value = true

  try {
    const response = await fetch(currentImageUrl.value)
    const blob = await response.blob()

    if (canShare.value && navigator.share) {
      const file = new File([blob], `${currentImage.value.filename || 'image'}.jpg`, { type: blob.type })

      await navigator.share({
        title: t('download.shareTitle'),
        text: t('download.shareText'),
        files: [file]
      })
    } else {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${currentImage.value.filename || 'image'}.jpg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  } catch (error) {
    console.error('Error downloading/sharing image:', error)
    alert(t('image.errorDownload'))
  } finally {
    downloading.value = false
  }
}

// Touch/Mouse swipe handling
const handleTouchStart = (e) => {
  if (props.images.length <= 1) return
  startX.value = e.touches[0].clientX
  isDragging.value = true
}

const handleTouchMove = (e) => {
  if (!isDragging.value || props.images.length <= 1) return
  const currentX = e.touches[0].clientX
  const diff = currentX - startX.value

  // Limit swipe offset
  if ((diff > 0 && currentIndex.value === 0) || (diff < 0 && currentIndex.value === props.images.length - 1)) {
    swipeOffset.value = diff * 0.3 // Reduced resistance at boundaries
  } else {
    swipeOffset.value = diff
  }
}

const handleTouchEnd = () => {
  if (!isDragging.value || props.images.length <= 1) return

  const threshold = 100 // Minimum swipe distance to trigger navigation

  if (swipeOffset.value > threshold) {
    previous()
  } else if (swipeOffset.value < -threshold) {
    next()
  }

  // Reset with transition
  setTimeout(() => {
    swipeOffset.value = 0
    isDragging.value = false
  }, 50)
}

// Mouse drag support for desktop
const handleMouseDown = (e) => {
  if (props.images.length <= 1) return
  startX.value = e.clientX
  isDragging.value = true
}

const handleMouseMove = (e) => {
  if (!isDragging.value || props.images.length <= 1) return
  const currentX = e.clientX
  const diff = currentX - startX.value

  if ((diff > 0 && currentIndex.value === 0) || (diff < 0 && currentIndex.value === props.images.length - 1)) {
    swipeOffset.value = diff * 0.3
  } else {
    swipeOffset.value = diff
  }
}

const handleMouseEnd = () => {
  if (!isDragging.value || props.images.length <= 1) return

  const threshold = 100

  if (swipeOffset.value > threshold) {
    previous()
  } else if (swipeOffset.value < -threshold) {
    next()
  }

  // Reset with transition
  setTimeout(() => {
    swipeOffset.value = 0
    isDragging.value = false
  }, 50)
}

// Keyboard navigation
const handleKeydown = (e) => {
  if (!props.isOpen) return

  switch (e.key) {
    case 'Escape':
      close()
      break
    case 'ArrowLeft':
      previous()
      break
    case 'ArrowRight':
      next()
      break
  }
}

// Watch for index changes from parent
watch(() => props.initialIndex, (newIndex) => {
  currentIndex.value = newIndex
})

// Lifecycle
onMounted(() => {
  canShare.value = navigator.share !== undefined
  document.addEventListener('keydown', handleKeydown)

  // Prevent body scroll when lightbox is open
  if (props.isOpen) {
    document.body.style.overflow = 'hidden'
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})

// Watch isOpen to manage body scroll
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})
</script>

<style scoped>
/* Overlay Background */
.bg-black\/95 {
  background-color: var(--theme-grid-overlay-bg-hover);
}

/* Close Button - Ghost Style */
.bg-white\/10 {
  background-color: var(--theme-button-ghost-bg);
  border-color: var(--theme-button-ghost-border);
}

.bg-white\/10:hover {
  background-color: var(--theme-button-ghost-bg-hover);
}

/* Navigation Buttons - Secondary Style */
button.bg-white\/10 {
  background-color: var(--theme-button-secondary-bg);
  border-color: var(--theme-button-secondary-border);
}

button.bg-white\/10:hover {
  background-color: var(--theme-button-secondary-bg-hover);
}

/* Download Button - Primary Style */
.button-primary{
  background-color: var(--theme-button-primary-bg);
  color: var(--color-theme-text-inverted);
  border-color: var(--theme-button-primary-border);
}
.button-primary:hover{
  background-color: var(--theme-button-primary-bg-hover);
}

/* Privacy Notice Card */
.bg-white\/10.backdrop-blur-md {
  background-color: var(--theme-grid-card-bg);
  border: 1px solid var(--theme-grid-card-border);
}

/* Lightbox modal transitions */
.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity 0.3s ease;
}

.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}

/* Slide transitions for image navigation */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Prevent text selection during drag */
.select-none {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.loading-spinner{
  border-color: color-mix(in srgb, var(--color-theme-text-inverted) 30%, transparent);
  border-top-color: var(--color-theme-text-inverted);
}

.text-theme-primary{
  color: var(--color-theme-primary-text);
}
</style>